import { Controller } from "@hotwired/stimulus"
import { generateHTML } from "@tiptap/html"
import StarterKit from "@tiptap/starter-kit"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import Highlight from "@tiptap/extension-highlight"
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight"
import { createLowlight, common } from "lowlight"

/** Turn lowlight hast root into HTML string for injection into code blocks */
function hastToHtml(node) {
  if (!node) return ""
  if (node.type === "text") {
    const div = document.createElement("div")
    div.textContent = node.value ?? ""
    return div.innerHTML
  }
  if (node.type === "root") {
    return (node.children ?? []).map(hastToHtml).join("")
  }
  if (node.type === "element") {
    const tag = node.tagName ?? "span"
    const attrs = (node.properties ?? {})
    const classList = attrs.className
    const classAttr = Array.isArray(classList) && classList.length ? ` class="${classList.join(" ")}"` : ""
    const children = (node.children ?? []).map(hastToHtml).join("")
    return `<${tag}${classAttr}>${children}</${tag}>`
  }
  return ""
}

/** Heuristic: paragraph text that looks like code → treat as code block */
function looksLikeCode(text) {
  if (!text || typeof text !== "string") return false
  const t = text.trim()
  if (!t.length) return false
  const codeStarts = /^(def |class |function |import |from |const |let |var |if |for |while |return |print\s*\(|puts |func |fn |pub |async |await |try:|@|#include|<\?php)/m
  const hasIndentOrBraces = /^\s{2,}/m.test(t) || /[\{\}\(\)\[\];]/.test(t)
  const singleLineLooksLikeCode = codeStarts.test(t) || (t.includes("(") && t.includes(")") && t.includes(":") && t.length < 200)
  return singleLineLooksLikeCode || (t.includes("\n") && hasIndentOrBraces) || codeStarts.test(t)
}

export default class extends Controller {
  static values = { content: String }
  static targets = ["payload", "output"]

  connect() {
    const rawContent = this.getRawContent()
    if (!rawContent) return
    let json
    try {
      json = JSON.parse(rawContent)
      if (typeof json === "string") {
        json = JSON.parse(json)
      }
    } catch (_) {
      return
    }

    const lowlight = createLowlight(common)
    const CodeBlock = CodeBlockLowlight.extend({
      addAttributes() {
        return {
          language: {
            default: null,
            renderHTML: (attrs) => ({
              "data-language": attrs.language || "Plain"
            })
          }
        }
      }
    })
    let html = generateHTML(json, [
      StarterKit.configure({ link: false, codeBlock: false }),
      Highlight,
      Image,
      Link,
      CodeBlock.configure({ lowlight })
    ])

    html = this.paragraphsToCodeBlocks(html)
    html = this.highlightCodeBlocks(html, lowlight)

    const out = this.hasOutputTarget ? this.outputTarget : this.element
    out.innerHTML = html
    out.querySelectorAll("img").forEach((img) => {
      img.loading = "lazy"
    })
  }

  paragraphsToCodeBlocks(html) {
    const wrap = document.createElement("div")
    wrap.innerHTML = html
    wrap.querySelectorAll("p").forEach((p) => {
      const text = (p.textContent ?? "").trim()
      if (!text || !looksLikeCode(text)) return
      const pre = document.createElement("pre")
      pre.setAttribute("data-language", "plain")
      const code = document.createElement("code")
      code.textContent = text
      pre.appendChild(code)
      p.replaceWith(pre)
    })
    return wrap.innerHTML
  }

  highlightCodeBlocks(html, lowlight) {
    const wrap = document.createElement("div")
    wrap.innerHTML = html
    const pres = wrap.querySelectorAll("pre")
    pres.forEach((pre) => {
      const code = pre.querySelector("code")
      if (!code) return
      const lang = (pre.dataset.language || "").trim().toLowerCase()
      const raw = code.textContent ?? ""
      if (!raw) return
      let root
      try {
        if (lang && lang !== "plain" && lowlight.listLanguages().includes(lang)) {
          root = lowlight.highlight(lang, raw)
        } else {
          root = lowlight.highlightAuto(raw)
        }
      } catch (_) {
        return
      }
      if (root?.children?.length > 0) {
        code.innerHTML = hastToHtml(root)
      }
    })
    return wrap.innerHTML
  }

  getRawContent() {
    if (this.hasPayloadTarget) {
      return this.payloadTarget.textContent?.trim()
    }
    if (!this.hasContentValue) return ""
    return this.decodeContentValue(this.contentValue)
  }

  decodeContentValue(value) {
    if (!value || typeof value !== "string") return value
    if (!value.includes("&")) return value
    const textarea = document.createElement("textarea")
    textarea.innerHTML = value
    return textarea.value
  }
}
